import type { ReactNode } from 'react'
import { cn } from '../../lib/cn'

export interface SubjectStickyFooterProps {
  text: string
  renderCta: (className: string) => ReactNode
  isVisible?: boolean
  /** `subject` keeps the black phone bar of the subject pages; `event` carries
   *  the indigo banner down to phones. */
  variant?: 'subject' | 'event'
}

export const SubjectStickyFooter = ({
  text,
  renderCta,
  isVisible = true,
  variant = 'subject',
}: SubjectStickyFooterProps) => {
  const isEvent = variant === 'event'

  return (
    <div
      // Opacity alone leaves the hidden bar in the tab order and the a11y tree,
      // so a keyboard user lands on an off-screen CTA.
      inert={!isVisible}
      className={`fixed inset-x-0 bottom-[-1px] md:bottom-5 z-50 transition-all duration-300 ${
        isVisible
          ? 'translate-y-0 opacity-100'
          : 'pointer-events-none translate-y-full opacity-0'
      }`}
    >
      <div
        className={cn(
          'md:mx-auto md:max-w-[600px] md:bg-edu-accent-indigo-banner',
          isEvent ? 'bg-edu-accent-indigo-banner' : 'bg-black',
        )}
      >
        <div
          className={cn(
            'flex items-center justify-between md:py-4 md:px-5 gap-4 px-6 py-4',
            isEvent && 'gap-8 px-5',
          )}
        >
          <span
            className={cn(
              'text-white',
              isEvent
                ? 'text-body-3 leading-body-3'
                : 'text-xs leading-snug md:text-[16px] md:font-normal md:leading-[120%] md:tracking-[0.48px]',
            )}
          >
            {text}
          </span>
          {renderCta(
            'flex shrink-0 items-center justify-center uppercase no-underline h-9 bg-edu-accent-indigo-medium px-3 text-sm font-medium tracking-[0.7px] text-white md:h-auto md:bg-white md:py-[15px] md:px-[30px] md:text-[14px] md:leading-[120%] md:font-semibold md:tracking-normal md:text-edu-accent-indigo-base',
          )}
        </div>
      </div>
    </div>
  )
}
